import PropTypes from "prop-types";
import React from "react";
import { useSelector } from "react-redux";
import { addTimeOffRequest, fetchLatestTimeOffRequestWOPFreezeEvent } from "../api/timeOffRequest.js";
import { selectUserSession } from "../slices/userSession/userSessionSlice.js";
import { ACTION_CANCEL_SUBMIT, ACTION_RETRACT, STATUS_CANCEL_SUBMITTED, STATUS_RETRACTED } from "../util/constants.js";
import { UNKNOWN_ACTION_MESSAGE } from "../util/messageConstants.js";
import { buildTimeOffRequestEventPayload } from "../util/timeOffRequestEventBuilder.js";
import { validateTimeOffRequestEventSequence } from "../util/timeOffRequestEventValidation.js";
import ConfirmButton from "./fundamentals/ConfirmButton.jsx";
import { isLWOPTOWOPRequest } from "../util/timeOffRequestUtils.js";
import { getIsoDateFormat } from "../util/dateTimeFormatter.js";

const TimeOffRequestEmployeeActionConfirm = (props) => {
  const userSession = useSelector(selectUserSession);

  const getActionConfirmButtonBasedOnActionSelected = (action) => {
    switch (action) {
      case ACTION_RETRACT:
        return getActionConfirmButton(STATUS_RETRACTED);
      case ACTION_CANCEL_SUBMIT:
        return getActionConfirmButton(STATUS_CANCEL_SUBMITTED);
      default:
        return UNKNOWN_ACTION_MESSAGE(props.actionSelected);
    }
  };

  const getActionConfirmButton = (statusName) => {
    return (
      <ConfirmButton
        variant="contained"
        color="primary"
        onClick={() => handleActionClicked(statusName)}
        isLoading={props.isActionLoading}
      >
        Yes
      </ConfirmButton>
    );
  };

  const prepareTimeOffRequestData = () => {
    let timeOffRequestData = cleanupReasonData({ ...props.timeOffRequest });

    let requestingShiftsObj = {
      timeOffRequestType: timeOffRequestData.timeOffRequestType,
      shiftStart: timeOffRequestData.shiftStart,
      shiftEnd: timeOffRequestData.shiftEnd,
    };
    if (isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType)) {
      requestingShiftsObj = {
        ...requestingShiftsObj,
        preferredWOPStart: timeOffRequestData.preferredWOPStart,
        preferredWOPEnd: timeOffRequestData.preferredWOPEnd,
        wopASAP: timeOffRequestData.wopASAP,
        phoneNumber: timeOffRequestData.phoneNumber,
        ...(timeOffRequestData.approvedWOPStart ? { approvedWOPStart: timeOffRequestData.approvedWOPStart } : {}),
        ...(timeOffRequestData.approvedWOPEnd ? { approvedWOPEnd: timeOffRequestData.approvedWOPEnd } : {}),
      };
    }
    timeOffRequestData.requestingShifts = [requestingShiftsObj];
    return timeOffRequestData;
  };

  const handleActionClicked = async (statusName) => {
    if (!props.isFormValidationSuccess()) {
      return;
    }

    if (isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType) && props.actionSelected === ACTION_RETRACT) {
      const freezeEventMessage = await getLatestWOPFreezeEvent();
      if (freezeEventMessage !== "") {
        props.onSetActionErrors(freezeEventMessage);
        props.onSetIsActionLoading(false);
        props.onSetIsActionError(true);
        return;
      }
    }

    props.onSetIsActionLoading(true);

    let timeOffRequestData = prepareTimeOffRequestData();

    let payload = buildTimeOffRequestEventPayload(statusName, timeOffRequestData, userSession);

    let timeOffRequestEventSequenceValidationErrors = await validateTimeOffRequestEventSequence(
      timeOffRequestData.torId,
      statusName
    );

    if (timeOffRequestEventSequenceValidationErrors !== "") {
      props.onSetActionErrors(timeOffRequestEventSequenceValidationErrors);
      props.onSetIsActionLoading(false);
      props.onSetIsActionError(true);
    } else {
      addTimeOffRequest(payload)
        .then((res) => res.json())
        .then((result) => {
          if (result.error) {
            console.error("Error");
            props.onSetActionErrors(result.error);
            props.onSetIsActionLoading(false);
            props.onSetIsActionError(true);
          } else if (result.newTimeOffRequestEvents.length > 0) {
            props.onClickDialogClose();
            if (props.onSetIsActionSuccess) {
              props.onSetIsActionSuccess(true);
            }
          } else {
            console.error("Unknown response");
          }
        });
    }
  };

  const getLatestWOPFreezeEvent = async () => {
    try {
      const resp = await fetchLatestTimeOffRequestWOPFreezeEvent(
        props.timeOffRequest.station,
        props.timeOffRequest.workgroup,
        getIsoDateFormat(props.timeOffRequest.shiftStart),
        props.timeOffRequest.timeOffRequestType
      );
      const result = await resp.json();
      if (result.latestWopFreezeEvent?.isFrozen) {
        return "Award is in-progress. You cannot retract this request at this time.";
      }
      return "";
    } catch (error) {
      console.error("Error fetching latest WOP freeze event:", error);
      return "Something went wrong. Please try again.";
    }
  };

  const cleanupReasonData = (timeOffRequest) => {
    if (timeOffRequest.cannedReason) {
      delete timeOffRequest["cannedReason"];
    }
    if (timeOffRequest.reasonComments) {
      delete timeOffRequest["reasonComments"];
    }
    return timeOffRequest;
  };

  return getActionConfirmButtonBasedOnActionSelected(props.actionSelected);
};

TimeOffRequestEmployeeActionConfirm.propTypes = {
  timeOffRequest: PropTypes.object.isRequired,
  actionSelected: PropTypes.string.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
  onSetIsActionError: PropTypes.func.isRequired,
  onSetActionErrors: PropTypes.func.isRequired,
  onSetIsActionLoading: PropTypes.func.isRequired,
  onSetIsActionSuccess: PropTypes.func.isRequired,
  isActionLoading: PropTypes.bool.isRequired,
  isFormValidationSuccess: PropTypes.func.isRequired,
};

export default TimeOffRequestEmployeeActionConfirm;
